import './styles.scss';

import { Button, Link } from '@ets-global/b2c-website-ui';
import classNames from 'classnames';
import { List as ImmutableList } from 'immutable';
import React, { Fragment, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { TinyText } from '../../components/Base/Text';
import { SmallTitle } from '../../components/Base/Title';
import { List } from '../../components/Element';
import HeaderMeta from '../../components/HeaderMeta';
import { default as LoginComponent } from '../../components/Login';
import { useAuth, useBasketCookie } from '../../hooks';
import useDataLayer from '../../hooks/useDataLayer';
import { paths } from '../../paths';

const Login = ({ targetLocation }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { getBasket } = useBasketCookie();
  const registrationId = getBasket();
  const { login } = useAuth();
  const { pushEventLoadedAccountCreationFunnel, pushEventLoginOrRegister, pushEventFormError } = useDataLayer();
  const { search } = location;
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    pushEventLoadedAccountCreationFunnel({
      stepName: 'login page',
      stepNumber: '1',
    });
  }, []);

  const createAccountAdvantages = ImmutableList(
    [
      'login-panel.create-account.advantages.products-and-services',
      'login-panel.create-account.advantages.save-information',
      'login-panel.create-account.advantages.purchase-history',
      'login-panel.create-account.advantages.track-purchase',
    ].map((item) => t(item))
  );

  const onSubmit = async (data, actions) => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('b2c_website_login');

    try {
      await login({
        userData: {
          username: data.username,
          password: data.password,
          recaptchaToken: token,
          rememberMe: Boolean(data.rememberMe),
        },
      });

      pushEventLoginOrRegister();

      // redirection done by CantAccessLogged HOC
    } catch (error) {
      const errorMessage = t(`login.${error.message}`);
      actions.setErrors({ form: errorMessage }); // Force error message for login
      pushEventFormError(errorMessage);
    }
    actions.setSubmitting(false);
  };

  return (
    <Fragment>
      <HeaderMeta page={'login'} />
      <main className={'main'}>
        <div id="login-panel" className={classNames('login-panel', 'spacer-menu')}>
          <div className={'container'}>
            <div className={'login-panel__row'}>
              <div className={'login-panel__column'}>
                <SmallTitle>
                  <Trans>login-panel.login.title</Trans>
                </SmallTitle>
                <TinyText>
                  <Trans>login-panel.login.intro</Trans>
                </TinyText>
                <LoginComponent onSubmit={onSubmit} />
              </div>
              <div className={'login-panel__column'}>
                <SmallTitle>
                  <Trans>login-panel.create-account.title</Trans>
                </SmallTitle>
                <TinyText>
                  <Trans>login-panel.create-account.advantages.title</Trans>
                </TinyText>
                <List
                  items={createAccountAdvantages}
                  listClassNames={'login-panel__list'}
                  itemClassNames={'login-panel__list-item'}
                  translate={true}
                />
                <Button
                  data-cypress="register-link"
                  to={paths.REGISTRATION}
                  search={search}
                  aria-label={t('login-panel.create-account.button')}
                >
                  <Trans>login-panel.create-account.button</Trans>
                </Button>
                {targetLocation && registrationId && (
                  <Link
                    to={targetLocation}
                    className={'login-panel__skip-link'}
                    icon={'arrow'}
                    data-cypress="button-skip-login"
                    aria-label={t('login.link.skip')}
                  >
                    <Trans>login.link.skip</Trans>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </Fragment>
  );
};

export default Login;
